/**
 * Move dispatcher and per-action handlers.
 *
 *   applyMove(state, move, rng?) -> next state
 *
 * Pure: never mutates `state`. Validates the move and throws `EngineError`
 * with a typed code on rejection.
 *
 * The four `Move` kinds are:
 *   - `take-pyramid-ticket` — draw a die from the pyramid at random; move the
 *     selected camel; record the roll. Triggers leg-end (and possibly
 *     race-end) when the pyramid is empty or a racing camel finishes.
 *   - `take-leg-bet`        — claim the top leg-bet ticket for a color.
 *   - `place-race-bet`      — place an available race-bet card on a pile.
 *   - `continue-leg`        — dismiss the leg-end review and start the next
 *                             leg. Status must be `'leg_end'`.
 */

import type {
  CamelUpPlayer,
  CamelUpState,
  CrazyColor,
  DiceResult,
  DieId,
  LegBetTicket,
  RaceBetSlot,
  RacingColor,
} from '../types'
import { PYRAMID_DICE, defaultLegBetStack } from './constants'
import {
  moveCrazyBackward,
  moveRacingForward,
  rankRacingCamels,
} from './movement'
import { scoreLeg, scoreRace } from './scoring'
import { rollCrazyColor, rollD3 } from './setup'
import { EngineError } from './types'
import type { Move } from './types'

export interface ApplyMoveArgs {
  state: CamelUpState
  move: Move
  /** Optional deterministic RNG. Defaults to `Math.random`. */
  rng?: () => number
}

export interface ApplyMoveResult {
  state: CamelUpState
}

export function applyMove(args: ApplyMoveArgs): ApplyMoveResult {
  const { state, move, rng = Math.random } = args

  // Continue-leg works only from leg_end; all other moves require active.
  if (move.kind === 'continue-leg') {
    return { state: applyContinueLeg(state, move.uid) }
  }

  if (state.status === 'finished') {
    throw new EngineError('GAME_OVER', 'Game has ended.')
  }
  if (state.status !== 'active') {
    throw new EngineError(
      'WRONG_STATUS',
      `Move ${move.kind} requires status='active' (got '${state.status}').`,
    )
  }
  // Only the active player may act.
  const activeUid = state.seatOrder[state.activeIndex]
  if (move.uid !== activeUid) {
    throw new EngineError(
      'NOT_YOUR_TURN',
      `It's ${activeUid}'s turn, not ${move.uid}'s.`,
    )
  }
  if (!state.players[move.uid]) {
    throw new EngineError('UNKNOWN_PLAYER', `Unknown player ${move.uid}.`)
  }

  switch (move.kind) {
    case 'take-pyramid-ticket':
      return { state: applyTakePyramidTicket(state, move.uid, rng) }
    case 'take-leg-bet':
      return { state: applyTakeLegBet(state, move.uid, move.color) }
    case 'place-race-bet':
      return {
        state: applyPlaceRaceBet(state, move.uid, move.pile, move.color),
      }
  }
}

// ---------------------------------------------------------------------------
// take-pyramid-ticket
// ---------------------------------------------------------------------------

function applyTakePyramidTicket(
  state: CamelUpState,
  uid: string,
  rng: () => number,
): CamelUpState {
  if (state.pyramidDice.length === 0) {
    throw new EngineError('PYRAMID_EMPTY', 'No dice left in the pyramid.')
  }

  // Pick a random die from the pyramid.
  const pyramidIdx = Math.floor(rng() * state.pyramidDice.length)
  const die: DieId = state.pyramidDice[pyramidIdx]
  const value = rollD3(rng)

  let crazyColor: CrazyColor | undefined
  let afterMovement: CamelUpState
  let raceEndingFinish = false

  if (die === 'grey') {
    crazyColor = rollCrazyColor(rng)
    const result = moveCrazyBackward(state, crazyColor, value)
    afterMovement = result.state
  } else {
    const result = moveRacingForward(state, die, value)
    afterMovement = result.state
    raceEndingFinish = result.finished
  }

  const roll: DiceResult = { die, value, byUid: uid }
  if (crazyColor !== undefined) roll.crazyColor = crazyColor

  // Remove the drawn die from the pyramid; tally pyramid ticket for player.
  const nextPyramid = [...afterMovement.pyramidDice]
  nextPyramid.splice(pyramidIdx, 1)
  const nextRolled = [...afterMovement.rolledThisLeg, roll]
  const nextPlayer: CamelUpPlayer = {
    ...afterMovement.players[uid],
    pyramidTicketsThisLeg: afterMovement.players[uid].pyramidTicketsThisLeg + 1,
  }
  const nextPlayers = { ...afterMovement.players, [uid]: nextPlayer }

  let nextState: CamelUpState = {
    ...afterMovement,
    pyramidDice: nextPyramid,
    rolledThisLeg: nextRolled,
    players: nextPlayers,
  }

  // Race end takes precedence over leg end: if a racing camel crossed the
  // line, the current leg is scored as if it ended now, then race bets
  // resolve, then status flips to 'finished'.
  if (raceEndingFinish) {
    nextState = endRace(nextState)
    return nextState
  }

  // Leg end: as soon as 5 of the 6 dice have been rolled (one die left in
  // the pyramid). The leftover die is discarded; the pyramid refills next leg.
  if (nextPyramid.length <= 1) {
    nextState = endLeg(nextState)
    return nextState
  }

  // Otherwise advance turn.
  return advanceTurn(nextState)
}

// ---------------------------------------------------------------------------
// take-leg-bet
// ---------------------------------------------------------------------------

function applyTakeLegBet(
  state: CamelUpState,
  uid: string,
  color: RacingColor,
): CamelUpState {
  const stack = state.legBetStacks[color]
  if (!stack || stack.length === 0) {
    throw new EngineError(
      'NO_TICKETS_LEFT',
      `No ${color} leg-bet tickets remaining.`,
    )
  }
  const [ticket, ...rest] = stack
  const nextStacks = { ...state.legBetStacks, [color]: rest }
  const player = state.players[uid]
  const nextPlayer: CamelUpPlayer = {
    ...player,
    legBets: [...player.legBets, ticket],
  }
  const nextPlayers = { ...state.players, [uid]: nextPlayer }
  return advanceTurn({
    ...state,
    legBetStacks: nextStacks,
    players: nextPlayers,
  })
}

// ---------------------------------------------------------------------------
// place-race-bet
// ---------------------------------------------------------------------------

function applyPlaceRaceBet(
  state: CamelUpState,
  uid: string,
  pile: 'winner' | 'loser',
  color: RacingColor,
): CamelUpState {
  const player = state.players[uid]
  if (!player.availableRaceCards.includes(color)) {
    throw new EngineError(
      'CARD_NOT_AVAILABLE',
      `Player ${uid} doesn't hold a ${color} race-bet card.`,
    )
  }
  const slot: RaceBetSlot = { uid, color }
  const nextRacePiles = {
    ...state.racePiles,
    [pile]: [...state.racePiles[pile], slot],
  }
  const nextPlayer: CamelUpPlayer = {
    ...player,
    availableRaceCards: player.availableRaceCards.filter((c) => c !== color),
  }
  const nextPlayers = { ...state.players, [uid]: nextPlayer }
  return advanceTurn({
    ...state,
    racePiles: nextRacePiles,
    players: nextPlayers,
  })
}

// ---------------------------------------------------------------------------
// continue-leg (leg_end -> active)
// ---------------------------------------------------------------------------

function applyContinueLeg(state: CamelUpState, uid: string): CamelUpState {
  if (state.status !== 'leg_end') {
    throw new EngineError(
      'WRONG_STATUS',
      `continue-leg requires status='leg_end' (got '${state.status}').`,
    )
  }
  if (!state.players[uid]) {
    throw new EngineError('UNKNOWN_PLAYER', `Unknown player ${uid}.`)
  }

  // Refill pyramid, reset per-leg state, advance turn order to the next
  // player who hasn't acted in the new leg.
  const refreshedPlayers: Record<string, CamelUpPlayer> = {}
  for (const u of Object.keys(state.players)) {
    refreshedPlayers[u] = {
      ...state.players[u],
      legBets: [],
      pyramidTicketsThisLeg: 0,
    }
  }
  const freshLegBetStacks: CamelUpState['legBetStacks'] = {
    red: defaultLegBetStack('red'),
    yellow: defaultLegBetStack('yellow'),
    green: defaultLegBetStack('green'),
    blue: defaultLegBetStack('blue'),
    purple: defaultLegBetStack('purple'),
  }
  // Turn order continues from where it was when the pyramid emptied —
  // the player after the one who drew the final die.
  const nextActiveIndex = (state.activeIndex + 1) % state.seatOrder.length

  return {
    ...state,
    status: 'active',
    leg: state.leg + 1,
    activeIndex: nextActiveIndex,
    pyramidDice: [...PYRAMID_DICE],
    rolledThisLeg: [],
    legBetStacks: freshLegBetStacks,
    racePiles: state.racePiles, // race-bet piles persist across legs
    players: refreshedPlayers,
    legReview: null,
  }
}

// ---------------------------------------------------------------------------
// turn advance, leg end, race end
// ---------------------------------------------------------------------------

function advanceTurn(state: CamelUpState): CamelUpState {
  return {
    ...state,
    activeIndex: (state.activeIndex + 1) % state.seatOrder.length,
  }
}

/**
 * Score the current leg, apply coin deltas, transition to status='leg_end'.
 *
 * Does NOT advance leg counter / refill the pyramid — that happens on the
 * subsequent `continue-leg` move.
 */
function endLeg(state: CamelUpState): CamelUpState {
  const { review, deltas } = scoreLeg(state)
  const nextPlayers: Record<string, CamelUpPlayer> = {}
  for (const uid of Object.keys(state.players)) {
    nextPlayers[uid] = {
      ...state.players[uid],
      coins: Math.max(0, state.players[uid].coins + (deltas[uid] ?? 0)),
    }
  }
  return {
    ...state,
    status: 'leg_end',
    players: nextPlayers,
    legReview: review,
  }
}

/**
 * Score the leg, then resolve race bets, set status='finished'.
 *
 * Caller has already applied movement that triggered the finish; this just
 * resolves both rounds of scoring and pick the overall winner.
 */
function endRace(state: CamelUpState): CamelUpState {
  // Leg scoring first.
  const leg = scoreLeg(state)
  const afterLegPlayers: Record<string, CamelUpPlayer> = {}
  for (const uid of Object.keys(state.players)) {
    afterLegPlayers[uid] = {
      ...state.players[uid],
      coins: Math.max(0, state.players[uid].coins + (leg.deltas[uid] ?? 0)),
    }
  }
  const afterLegState: CamelUpState = {
    ...state,
    players: afterLegPlayers,
    legReview: leg.review,
  }
  // Race scoring (operates on post-leg coins).
  const race = scoreRace(afterLegState)
  const finalPlayers: Record<string, CamelUpPlayer> = {}
  for (const uid of Object.keys(afterLegState.players)) {
    finalPlayers[uid] = {
      ...afterLegState.players[uid],
      coins: Math.max(
        0,
        afterLegState.players[uid].coins + (race.deltas[uid] ?? 0),
      ),
    }
  }
  return {
    ...afterLegState,
    status: 'finished',
    players: finalPlayers,
    raceReview: race.review,
    winner: race.winner,
  }
}

// Re-export the public ranking helper for tests / UIs.
export { rankRacingCamels }
export type { LegBetTicket, RaceBetSlot }
